/**
 * Regenerable Text Field Component for Puck Editor
 *
 * A custom Puck field that wraps text/textarea fields with AI regeneration capability.
 * Shows a "regenerate" button next to the field with the namespace key.
 *
 * Uses inline styles with CSS variable references so it inherits the host app's
 * dark mode theme without needing its own Tailwind build or <style> tag.
 */

import { AlertCircle, ChevronDown, Loader2, Search, Zap } from "lucide-react";
import React, { useState } from "react";
import { getEditorFieldGuidance, getEditorFieldSeoIndicator } from '../../lib/editor-field-guidance';
import type { EditorFieldMetadata } from '../../lib/editor-field-guidance';
import type { RegenerableFieldNamespace } from "../../config/field-namespaces";
import { getEditorFieldValidationWarnings } from "../../lib/editor-field-validation";
import { getWindowJobsiteConfig } from "../../types/runtime-config";

interface RegenerableTextFieldProps {
  value: string;
  onChange: (value: string) => void;
  namespace: RegenerableFieldNamespace;
  label?: string;
  multiline?: boolean;
  placeholder?: string;
  fieldMeta?: EditorFieldMetadata;
}

const RegenerableTextField: React.FC<RegenerableTextFieldProps> = ({
  value = "",
  onChange,
  namespace,
  label,
  multiline = false,
  placeholder,
  fieldMeta,
}) => {
  const [isRegenerating, setIsRegenerating] = useState(false),
    [isNotesOpen, setIsNotesOpen] = useState(false),
    [error, setError] = useState<string | null>(null),
    guidance = getEditorFieldGuidance(fieldMeta),
    seoIndicator = getEditorFieldSeoIndicator(fieldMeta),
    {business} = getWindowJobsiteConfig(),
    noteItems = [guidance.description, ...guidance.tips].filter((item): item is string =>
      Boolean(item),
    ),
    hasNotes = noteItems.length > 0,
    warnings = getEditorFieldValidationWarnings({
      business,
      metadata: fieldMeta,
      value,
    }),
    handleRegenerate = async () => {
      setIsRegenerating(true);
      setError(null);

      try {
        // Read site/page IDs from data attributes set by the editor
        const siteId = document.documentElement.dataset.editorSiteId,
          pageId = document.documentElement.dataset.editorPageId;

        if (!siteId || !pageId) {
          throw new Error("Editor context not available. Please try refreshing the page.");
        }

        // Parse namespace to extract componentType and fieldName
        // Format: "pageType.componentType.fieldName" (e.g., "home.hero.headline")
        // Or "componentType.fieldName" (e.g., "Hero.headline")
        const parts = namespace.split(".");
        let componentType: string,
          fieldName: string;

        if (parts.length >= 3) {
          componentType = capitalizeFirst(parts[1]);
          fieldName = parts.slice(2).join(".");
        } else if (parts.length === 2) {
          componentType = capitalizeFirst(parts[0]);
          fieldName = parts[1];
        } else {
          throw new Error("Invalid namespace format");
        }

        const apiUrl = import.meta.env.VITE_API_URL || "",
          url = `${apiUrl}/api/sites/${siteId}/pages/${pageId}/regenerate-field`,

          response = await fetch(url, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              componentType,
              fieldName,
              currentValue: value,
            }),
          });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to regenerate content");
        }

        const data = await response.json();

        if (data.valid && data.content) {
          onChange(data.content);
        } else {
          throw new Error(data.error || "Generated content was invalid");
        }
      } catch (error: any) {
        console.error("Regeneration failed:", error);
        setError(error.message || "Failed to regenerate content");
      } finally {
        setIsRegenerating(false);
      }
    },
    capitalizeFirst = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1),
    inputStyle: React.CSSProperties = {
      backgroundColor: "var(--muted, #f3f4f6)",
      border: "1px solid var(--border, #e2e8f0)",
      borderRadius: "6px",
      boxSizing: "border-box",
      color: "var(--foreground, #0f172a)",
      fontFamily: "inherit",
      fontSize: "14px",
      lineHeight: 1.5,
      outline: "none",
      padding: "8px 12px",
      width: "100%",
    };

  return (
    <div>
      {label && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
          <div style={{ alignItems: "center", display: "flex", gap: 8 }}>
            <label
              style={{ color: "var(--muted-foreground, #6b7280)", fontSize: 14, fontWeight: 500 }}
            >
              {label}
            </label>
            {seoIndicator?.signal === "critical" ? (
              <span
                title={seoIndicator.description}
                data-testid={`seo-signal-${seoIndicator.signal}`}
                style={{
                  alignItems: "center",
                  backgroundColor: "rgba(243, 244, 246, 0.9)",
                  border: "1px solid rgba(203, 213, 225, 0.9)",
                  borderRadius: 999,
                  color: "rgba(55, 65, 81, 0.85)",
                  display: "inline-flex",
                  fontSize: 10,
                  fontWeight: 600,
                  gap: 4,
                  lineHeight: 1,
                  padding: "2px 6px",
                }}
              >
                <Search size={10} />
                {seoIndicator.label}
              </span>
            ) : null}
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={isRegenerating}
              style={{
                alignItems: "center",
                backgroundColor: isRegenerating ? "#F3F4F6" : "#EEF2FF",
                border: `1px solid ${isRegenerating ? "#E5E7EB" : "#C7D2FE"}`,
                borderRadius: 4,
                color: isRegenerating ? "#9CA3AF" : "#6366F1",
                cursor: isRegenerating ? "not-allowed" : "pointer",
                display: "flex",
                fontSize: 11,
                fontWeight: 500,
                gap: 4,
                marginLeft: "auto",
                padding: "2px 6px",
              }}
            >
              {isRegenerating ? (
                <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} />
              ) : (
                <Zap size={12} />
              )}
              {isRegenerating ? "Generating..." : "Regenerate"}
            </button>
          </div>

          {(guidance.reassurance || hasNotes || warnings.length > 0) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div
                style={{
                  alignItems: "center",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px 10px",
                }}
              >
                {guidance.reassurance ? (
                  <p
                    style={{
                      color: "var(--muted-foreground, #6b7280)",
                      fontSize: 11,
                      lineHeight: 1.4,
                      margin: 0,
                    }}
                  >
                    {guidance.reassurance}
                  </p>
                ) : null}

                {hasNotes ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsNotesOpen((open) => !open);
                    }}
                    style={{
                      alignItems: "center",
                      background: "transparent",
                      border: "none",
                      color: "var(--muted-foreground, #6b7280)",
                      cursor: "pointer",
                      display: "inline-flex",
                      fontSize: 11,
                      fontWeight: 600,
                      gap: 4,
                      lineHeight: 1.4,
                      padding: 0,
                    }}
                  >
                    Notes
                    <ChevronDown
                      size={12}
                      style={{
                        transform: isNotesOpen ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 160ms ease",
                      }}
                    />
                  </button>
                ) : null}
              </div>

              {warnings.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {warnings.map((warning) => (
                    <div
                      key={`${label}-${warning.kind}-${warning.message}`}
                      style={{
                        alignItems: "flex-start",
                        color: "rgb(180, 83, 9)",
                        display: "flex",
                        fontSize: 11,
                        gap: 6,
                        lineHeight: 1.4,
                      }}
                    >
                      <AlertCircle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span>{warning.message}</span>
                    </div>
                  ))}
                </div>
              ) : null}

              {hasNotes && isNotesOpen ? (
                <div
                  style={{
                    borderLeft: "1px solid rgba(148, 163, 184, 0.45)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    paddingLeft: 12,
                  }}
                >
                  <ul
                    style={{
                      color: "var(--muted-foreground, #6b7280)",
                      fontSize: 11,
                      lineHeight: 1.5,
                      listStyle: "none",
                      margin: 0,
                      paddingLeft: 0,
                    }}
                  >
                    {noteItems.map((item) => (
                      <li
                        key={`${label}-${item}`}
                        style={{
                          alignItems: "flex-start",
                          display: "flex",
                          gap: 8,
                          marginTop: 2,
                        }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            backgroundColor: "rgba(107, 114, 128, 0.75)",
                            borderRadius: "999px",
                            flexShrink: 0,
                            height: 6,
                            marginTop: 6,
                            width: 6,
                          }}
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}

      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          placeholder={placeholder}
          rows={3}
          style={{ ...inputStyle, resize: "vertical" }}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          placeholder={placeholder}
          style={inputStyle}
        />
      )}

      {error && (
        <div
          style={{
            backgroundColor: "rgba(220, 38, 38, 0.1)",
            border: "1px solid rgba(220, 38, 38, 0.2)",
            borderRadius: 4,
            color: "var(--destructive, #dc2626)",
            fontSize: 12,
            marginTop: 6,
            padding: "6px 10px",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export { RegenerableTextField };
